import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import PageTitle from 'components/PageTitle';
import Table from 'components/Table';
import { graphql, createFragmentContainer } from 'react-relay';
import moment from 'moment';
import Button from 'components/Button/Button';
import { useRouter } from 'found';
import { MachineReports_machineReports } from './__generated__/MachineReports_machineReports.graphql';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: '50px 60px'
  },
  spacer: {
    marginBottom: 60
  },
  rowText: {
    fontSize: 13,
    textWrap: 'nowrap',
    fontWeight: 400
  }
}));

const MachineReports = (machineReports: MachineReports_machineReports) => {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const { router, match } = useRouter();

  // Use GraphQL data if available, otherwise empty array
  const reports = machineReports?.edges || [];

  const pageUrl = (
    page?: { offset: number; limit: number },
    searchQuery?: string | null
  ) => {
    if (!searchQuery) {
      if (searchQuery === '') {
        searchQuery = '';
      } else {
        searchQuery = match.location.query.search ?? '';
      }
    }

    if (!page) {
      if (match.location.query.offset) {
        page = {
          offset: parseInt(match.location.query.offset),
          limit: parseInt(match.location.query.limit)
        };
      }
    }

    let url = `/app/machine-reports?`;

    if (page) {
      url += `offset=${page.offset}&limit=${page.limit}&`;
    }

    if (searchQuery) {
      url += `search=${searchQuery}&`;
    }

    return url;
  };

  const onNextPage = () => {
    const { offset, limit, total } = machineReports.pageInfo;

    if (offset + limit >= total) {
      return;
    }

    router.push(pageUrl({ offset: Math.min(offset + limit, total), limit }));
  };

  const onPreviousPage = () => {
    const { offset, limit } = machineReports.pageInfo;
    router.push(pageUrl({ offset: Math.max(offset - limit, 0), limit }));
  };

  const searchTimeout = React.useRef<ReturnType<typeof setTimeout>>();

  const onSearch = (query: string) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      router.push(pageUrl({ offset: 0, limit: DEFAULT_LIMIT }, query));
    }, 300);
  };

  const formatRow = [
    {
      header: 'Machine Name',
      minWidth: 200,
      format: (report: any) => <div>{report.machine?.name || 'Unknown'}</div>
    },
    {
      header: 'Report Title',
      minWidth: 250,
      format: (report: any) => <div>{report.title || 'Unknown'}</div>
    },
    {
      header: 'Date',
      minWidth: 160,
      format: (report: any) => (
        <div>
          {report.reportDate
            ? moment(report.reportDate).format('MMM DD, YYYY')
            : 'Unknown'}
        </div>
      )
    },
    {
      header: 'Download',
      minWidth: 160,
      format: (report: any) => (
        <div>
          {report.previewURL ? (
            <Button
              archetype="flat"
              onClick={() => {
                window.open(report.previewURL ?? undefined, '_blank');
              }}
            >
              View Report
            </Button>
          ) : (
            <div className={classes.rowText}>Not available</div>
          )}
        </div>
      )
    }
  ];

  return (
    <div className={classes.root}>
      <PageTitle
        title="Machine Reports"
        subTitle="View and manage machine inspection reports."
      />

      <div className={classes.spacer} />

      <Table
        tableName="machine reports"
        data={reports}
        formatRow={formatRow}
        onNextPage={onNextPage}
        onPreviousPage={onPreviousPage}
        pageInfo={machineReports?.pageInfo}
        onSearch={onSearch}
        searchValue={match.location.query.search}
        emptyTableText="No machine reports found. Reports will appear here once created."
        padding={{ vertical: 10 }}
      />
    </div>
  );
};

export const DEFAULT_LIMIT = 25;

export default createFragmentContainer(MachineReports, {
  machineReports: graphql`
    fragment MachineReports_machineReports on MachineReportPage {
      edges {
        uuid
        machine {
          name
        }
        title
        reportDate
        previewURL
      }
      pageInfo {
        total
        given
        offset
        limit
      }
    }
  `
});
